import Job from 'jobs/Job'
import { action, computed, observable, makeObservable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import Member from 'iam/models/Member'
import { FindMembers } from 'iam/MemberStore'
import { BehaviorSubject } from 'rxjs'
import { CancellationToken } from 'infra/cancellation'

export default class ClientInputEditorViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Client.
   */
  @observable
  client?: Member = undefined

  @observable
  jobs: Job[] = []

  constructor(private findMembers: FindMembers) {
    makeObservable(this)
  }

  /**
   * Validation context for client.
   */
  validationClient = validationContext(this, {
    client: [required('Client is required')],
  })

  /**
   * Getter for the current job client.
   */
  @computed
  get currentClient() {
    return this.jobs[0].owner
  }

  /**
   * Sets client to transfer.
   */
  @action.bound
  setClient(client?: Member) {
    this.client = client
  }

  /**
   * Indicates if client is valid.
   */
  @action.bound
  validateClient() {
    return this.validationClient.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validationClient.reset()
    this.client = undefined
  }

  @action
  activate(jobs: Job[]) {
    this._active.next(true)
    this.jobs = jobs
  }

  deactivate() {
    this._active.next(false)
  }

  /**
   * Fetches clients.
   */
  fetchClients = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const paged = await this.findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspace_id: this.jobs[0].currentMemberWorkspaceId,
        search_text: searchText,
      },
      cancellationToken,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }
}
